/**
 * NZSC-4281 자산원클릭 등기변동 모니터링 — 계약 상태·문의 Vuex 모듈.
 *
 * 계약 상태(GET /v1/sherlock/monitoring/contract)는 사이드바(메뉴·플랜 카드)·구독 정보·진입 분기가
 * 함께 읽는다. 한 화면에서 여러 컴포넌트가 동시에 부르면 진행 중인 요청 하나를 같이 기다린다(중복 호출 방지).
 *
 * 응답: { state: 'NONE'|'ACTIVE'|'ENDED', scheduled, startDate, endDate ('YYYY-MM-DD'|null), targetLimit, registeredCount, remaining }
 * scheduled(api-public 1c39dbf23): 시작일 전 계약이면 true — state 는 ACTIVE(계약 사용자, 등록·수정·발급 허용, 조회는 시작일부터).
 * 시작 전에는 반영 결과가 없어 summary baseDate·baseTime 이 null — 화면은 계약 중과 같게 그린다('… 첫 결과 반영' 문구).
 */
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import {
  callDeleteApi,
  callGetApi,
  callPatchApi,
  callPostApi,
  callPostBlobApi,
  callPostFormDataApi,
} from "@/utils/apiWrapper";

export const MONITORING_CONTRACT_STATE = {
  NONE: "NONE",
  ACTIVE: "ACTIVE",
  ENDED: "ENDED",
};

// 화면 정본(HTML 문의 화면)의 관심 종류 값 → API module 값.
export const MONITORING_INQUIRY_MODULE = {
  regular: "REGULAR",
  oneoff: "ONEOFF",
};

let contractRequest = null;

// apiWrapper 의 errorMutation 이름 — 실제 mutation 은 없고, 요청마다 fnErrorBox 가 실패 본문을 가로채 그 요청 결과로 돌려준다.
const REQUEST_ERROR = "MONITORING_REQUEST_ERROR";

/**
 * 요청마다 실패 본문을 따로 받는다 — 동시에 나간 요청(요약·목록 등)이 서로의 오류를 덮지 않게 공용 칸 없이 각 요청이 자기 오류로 판정한다.
 */
const fnErrorBox = commit => {
  const box = { error: null };
  box.commit = (type, payload, ...rest) => {
    if (type === REQUEST_ERROR) {
      box.error = payload || null;
      return undefined;
    }
    return commit(type, payload, ...rest);
  };
  return box;
};

/**
 * 응답 객체면 성공, 아니면 이 요청의 실패 본문({code, message})을 함께 돌려준다(화면 안내·7323 새로 읽기·7300 분기용).
 * body 는 실패 본문 전체 — 서버가 더 싣는 값(7325 seqs·registeredCount 등)을 읽는다.
 */
const fnMonitoringResult = (res, box) =>
  res && typeof res === "object"
    ? { ok: true, data: res }
    : {
        ok: false,
        code: box.error?.code ?? null,
        message: box.error?.message || null,
        body: box.error || null,
      };

export const state = {
  // null = 아직 모름(조회 전·실패). 실패 시 계약 없음으로 단정하지 않는다.
  _monitoringContract: null,
  // NZSC-4281 C-2: 진입 시 소유자 확인 팝업을 이번 방문(앱 상태)에서 이미 띄웠는지 — 정본 ocSeen.
  _monitoringOwnerCheckSeen: false,
  // NZSC-4281 C-4: 발급(이용권 차감)은 됐지만 담보물과 잇지 못한 담보물 번호(7322) — 이번 방문 동안 다시 발급을 막는다(이중 차감 방지).
  _monitoringIssueBlockedSeqs: [],
  // NZSC-4281: 사이드바 '등기변동 모니터링'을 누른 횟수 — 이미 진입 화면에 있을 때(같은 주소라 다시 열리지 않음)
  // 계약 조회에 실패해 있으면 이 값이 바뀔 때 다시 조회한다.
  _monitoringEntryNonce: 0,
};

export const getters = {
  getMonitoringContractState: state => state._monitoringContract?.state || null,
  // 계약 중·이용 종료 모두 '계약 사용자'(플랜 카드 줄·구독 정보 계약 카드 노출 기준).
  getIsMonitoringContracted: state =>
    [MONITORING_CONTRACT_STATE.ACTIVE, MONITORING_CONTRACT_STATE.ENDED].includes(
      state._monitoringContract?.state,
    ),
  // 사이드바 플랜 카드 줄 값 (정본 quotaLabel): 배정 건수, 이용 종료면 '종료'.
  getMonitoringQuotaLabel: state => {
    const contract = state._monitoringContract;
    if (contract?.state === MONITORING_CONTRACT_STATE.ENDED) return "종료";
    return `${(Number(contract?.targetLimit) || 0).toLocaleString()}건`;
  },
};

export const mutations = {
  SET_MONITORING_CONTRACT(state, contract) {
    state._monitoringContract = contract || null;
  },
  SET_MONITORING_OWNER_CHECK_SEEN(state, seen) {
    state._monitoringOwnerCheckSeen = !!seen;
  },
  BUMP_MONITORING_ENTRY_NONCE(state) {
    state._monitoringEntryNonce += 1;
  },
  ADD_MONITORING_ISSUE_BLOCKED(state, seqs) {
    state._monitoringIssueBlockedSeqs = Array.from(
      new Set(state._monitoringIssueBlockedSeqs.concat((seqs || []).map(Number))),
    );
  },
};

export const actions = {
  async acGetMonitoringContract({ commit }) {
    if (contractRequest) return contractRequest;
    contractRequest = (async () => {
      const res = await callGetApi({
        url: SherlockAcConstant.GET_SHERLOCK_MONITORING_CONTRACT,
        params: {},
        commit,
      });
      const contract = res && typeof res === "object" && res.state ? res : null;
      if (contract) commit("SET_MONITORING_CONTRACT", contract);
      return contract;
    })();
    try {
      return await contractRequest;
    } finally {
      contractRequest = null;
    }
  },

  /**
   * 도입 문의 접수. 필드명은 기존 엔터프라이즈 문의(POST /v1/sherlock/inquiry/enterprise) DTO 와 같게 snake_case.
   * user_no 는 AuthSherlockGuard 가 토큰으로 넣는다.
   */
  async acPostMonitoringInquiry({ commit }, form) {
    // 실패 본문(400 형식 오류 등)을 이 요청 결과로 돌려준다 — 화면이 서버 안내를 쓸 수 있으면 쓴다.
    const eb = fnErrorBox(commit);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_INQUIRY,
      data: {
        module: MONITORING_INQUIRY_MODULE[form.module] || MONITORING_INQUIRY_MODULE.regular,
        company_name: form.company,
        industry: form.biz,
        name: form.name,
        position: form.role,
        email: form.email,
        mobile: form.phone,
        expected_users: form.people,
        etc: form.etc,
      },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    // 성공은 true(본문 없음)일 수 있다 — {ok, code, message}
    return res ? { ok: true, data: res } : fnMonitoringResult(null, eb);
  },

  // ─── NZSC-4281 C-2 모니터링 화면 (요약·목록·변동 확인·소유자 수정·삭제·소유자 확인) ───
  // 목록·요약은 화면에서만 쓰므로 store 에 두지 않고 응답을 그대로 돌려준다({ok, data} | {ok:false, code, message}).

  /** GET /summary → {ok, data} | {ok:false, code, message}. data = {state,startDate,endDate,registeredCount,targetLimit,changedTargetCount,unchangedTargetCount,
   *  unconfirmedTargetCount,doneTargetCount,baseDate,baseTime,ownerCheckCount,scheduled} */
  async acGetMonitoringSummary({ commit }) {
    // 실패 본문({code, message})을 이 요청 결과로 돌려준다(7300 계약 없음이면 진입 분기를 다시 탄다).
    const eb = fnErrorBox(commit);
    const res = await callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_MONITORING_SUMMARY,
      params: {},
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /** GET /targets — group(unconfirmed|done|all)·q·sort·page·size → {ok, data: {group,q,sort,page,size,total,items}} | {ok:false, code, message} */
  async acGetMonitoringTargets({ commit }, { group, q, sort, page, size }) {
    const params = { group, page: String(page || 1), size: String(size || 20) };
    if (q) params.q = q;
    if (sort) params.sort = sort;
    const eb = fnErrorBox(commit);
    const res = await callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_MONITORING_TARGETS,
      params,
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /**
   * POST /targets/confirm — 화면에 보인 변동 건만 {seqs, cases:[{caseSeq, version}]}
   * → {ok, data: {caseCount, remaining, cases}} | {ok:false, code, message}
   */
  async acPostMonitoringConfirm({ commit }, { seqs, cases }) {
    const eb = fnErrorBox(commit);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_CONFIRM,
      data: { seqs, cases },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /** POST /targets/unconfirm — {seqs} → {ok, data: {caseCount, remaining, cases}} | {ok:false, code, message} */
  async acPostMonitoringUnconfirm({ commit }, { seqs }) {
    const eb = fnErrorBox(commit);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_UNCONFIRM,
      data: { seqs },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /**
   * PATCH /targets/:seq {unique_no?, owner_name?, expected_unique_no?, expected_owner_name?} → {ok, data} | {ok:false, code, message}.
   * 실패하면 서버 코드·문구(7303 고유번호 형식·7304 소유자 형식·7305 이미 등록된 고유번호·
   * 7323 그사이 다른 곳에서 바뀜 — 새로 읽고 다시 등)를 돌려준다.
   */
  async acPatchMonitoringTarget(
    { commit },
    { seq, uniqueNo, ownerName, expectedUniqueNo, expectedOwnerName, ownerCheck },
  ) {
    const eb = fnErrorBox(commit);
    const data = {};
    // 소유자 확인 팝업(직접 수정·소유자 수정 팝업)에서 저장 — 서버가 공동 소유자를 비운다(정본 saveFx co: null).
    // 목록 '등록 정보 수정'은 보내지 않는다(공동 소유자 유지).
    if (ownerCheck === true) data.owner_check = true;
    if (uniqueNo != null) data.unique_no = uniqueNo;
    if (ownerName != null) data.owner_name = ownerName;
    // 화면이 읽었던 값 — 그사이 다른 곳에서 바뀌었으면 서버가 7323(동시 수정 덮어쓰기 방지)
    if (expectedUniqueNo != null)
      data.expected_unique_no = String(expectedUniqueNo).replace(/[^0-9]/g, "");
    if (expectedOwnerName != null) data.expected_owner_name = expectedOwnerName;
    const res = await callPatchApi({
      url: `${SherlockAcConstant.SHERLOCK_MONITORING_TARGET}${seq}`,
      data,
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /** DELETE /targets {seqs} → {ok, data: {deletedCount, failedCount}} | {ok:false, code, message} */
  async acDeleteMonitoringTargets({ commit }, { seqs }) {
    const eb = fnErrorBox(commit);
    const res = await callDeleteApi({
      url: SherlockAcConstant.DELETE_SHERLOCK_MONITORING_TARGETS,
      data: { seqs },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /** GET /owner-check → {count, items:[{seq,address,uniqueNo,ownerName,coOwners,registryOwnerDisplay}]} */
  async acGetMonitoringOwnerCheck({ commit }) {
    return callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_MONITORING_OWNER_CHECK,
      params: {},
      commit,
    });
  },

  /**
   * POST /owner-check/export — 수정 필요 건만 담은 엑셀(.xlsx, 관리번호·고유번호·소유자·주소·등기 기록상 소유자)
   * → {ok, blob, filename} | {ok:false, error}
   */
  async acPostMonitoringOwnerCheckExport({ commit }) {
    const eb = fnErrorBox(commit);
    const res = await callPostBlobApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_OWNER_CHECK_EXPORT,
      data: {},
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    if (res.ok) return res;
    return { ok: false, error: eb.error };
  },

  /**
   * POST /owner-check/reupload (multipart file) — 고친 엑셀로 고유번호·소유자명 수정
   * → {ok, data: 등록 결과 모양 {source,total,ok,fmt,dup,over,overN,addr,failedN}} | {ok:false, error}.
   * 관리번호 오류 행은 fmt(형식 오류), 그사이 바뀐 건(7323)·pig 실패는 failedN.
   */
  async acPostMonitoringOwnerCheckReupload({ commit }, file) {
    const eb = fnErrorBox(commit);
    const formData = new FormData();
    formData.append("file", file);
    const res = await callPostFormDataApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_OWNER_CHECK_REUPLOAD,
      formData,
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    if (res?.ok && res.data && typeof res.data === "object") return { ok: true, data: res.data };
    return { ok: false, error: eb.error };
  },

  // ─── NZSC-4281 C-4 등기부등본 발급·발급본 ───

  /**
   * POST /issue {seqs, bulk} → {ok, data: {issues:[{issueSeq, seq, estateQuerySeq}], unlinked:[{seq, estateQuerySeq}], noticeCode}}
   * | {ok:false, code, message} (7106 이용권 부족 등 기존 발급 오류 그대로).
   */
  async acPostMonitoringIssue({ commit }, { seqs, bulk }) {
    const eb = fnErrorBox(commit);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_ISSUE,
      data: { seqs, bulk: !!bulk },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /**
   * GET /issue/result?seqs=발급 번호(쉼표) → {ok, data: {done, items:[{issueSeq, seq, status}], changed:[…]}}
   * | {ok:false, code, message}(7300 계약 없음이면 폴링을 멈춘다).
   */
  async acGetMonitoringIssueResult({ commit }, issueSeqs) {
    const eb = fnErrorBox(commit);
    const res = await callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_MONITORING_ISSUE_RESULT,
      params: { seqs: (issueSeqs || []).join(",") },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    return fnMonitoringResult(res, eb);
  },

  /**
   * GET /targets/:seq/documents → {seq, documents:[{issueSeq, estateQuerySeq, issuedDate, url(15분)}]} (실패는 null).
   * download=true 면 ?download=1 — 링크를 내려받기용(S3 ResponseContentDisposition=attachment)으로 달라는 요청
   * (api-public 계약 추가 필요: NZSC-4281 C-4 보고 참조. 지원 전에는 서버가 무시하고 보기 링크를 준다).
   */
  async acGetMonitoringDocuments({ commit }, { seq, download = false }) {
    return callGetApi({
      url: `${SherlockAcConstant.SHERLOCK_MONITORING_TARGET}${seq}/documents`,
      params: download ? { download: "1" } : {},
      commit,
    });
  },

  // ─── NZSC-4281 C-3 담보물 등록 창 ───
  // 실패하면 응답 본문 {code, message}(7300 계약 없음·7301 이용 종료·7307 행 수 초과·7308 파일 해석 실패·7310/7319 pig 등)를 돌려준다.

  /** POST /register/parse (multipart file) → {ok, data: {rows:[{uid,owner,addr}], result}} | {ok:false, error} */
  async acPostMonitoringRegisterParse({ commit }, file) {
    const eb = fnErrorBox(commit);
    const formData = new FormData();
    formData.append("file", file);
    const res = await callPostFormDataApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_REGISTER_PARSE,
      formData,
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    if (res?.ok && res.data && typeof res.data === "object") return { ok: true, data: res.data };
    return { ok: false, error: eb.error };
  },

  /** POST /register {rows, source} → {ok, data: result} | {ok:false, error}. rows 는 parse 가 돌려준 그대로(행 번호 = 위치). */
  async acPostMonitoringRegister({ commit }, { rows, source }) {
    const eb = fnErrorBox(commit);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_MONITORING_REGISTER,
      data: { rows, source },
      commit: eb.commit,
      errorMutation: REQUEST_ERROR,
    });
    if (res && typeof res === "object") return { ok: true, data: res };
    return { ok: false, error: eb.error };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
